import { HttpContext, HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { CbreLoggerToken } from '@cbre/logger';
import { throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

/**
 * Global HTTP timeout (CM-33544). During the 2026-08-24 incident the edge stack
 * held /cepapi connections open without responding; no call in the app had a
 * timeout, so render-gating guards/resolvers waited forever and their catchError
 * fallbacks never fired (a hang is not an error).
 *
 * The timeout MUST live here, at the HTTP layer, and not at the call sites:
 * the render-gating chains are memoised with shareReplay(1) (userLanguages$,
 * featureConfig$, userSettingsCache, ...). shareReplay only resets its poisoned
 * cache when the error originates INSIDE the shared source - i.e. from the HTTP
 * request itself. A call-site timeout one level up leaves the shared source
 * pending forever, so every retry re-hangs on the same dead observable.
 *
 * Endpoints that legitimately run long must opt out per request:
 *   http.get(url, { context: withHttpTimeout(120_000) })
 * A value of 0 disables the timeout for that request.
 *
 * Note: rxjs timeout() re-arms on every emission, so upload/download progress
 * events keep a slow-but-alive transfer from being killed.
 */
export const HTTP_TIMEOUT_MS = new HttpContextToken<number>(() => 60000);

/** Convenience for call sites: `{ context: withHttpTimeout(60_000) }`. */
export function withHttpTimeout(ms: number, context: HttpContext = new HttpContext()): HttpContext {
  return context.set(HTTP_TIMEOUT_MS, ms);
}

export const timeoutInterceptor: HttpInterceptorFn = (req, next) => {
  const logger = inject(CbreLoggerToken);
  const ms = req.context.get(HTTP_TIMEOUT_MS);
  if (ms <= 0) {
    return next(req);
  }
  return next(req).pipe(
    timeout(ms),
    catchError(error => {
      if (error?.name === 'TimeoutError') {
        logger.error({
          eventName: 'request timed out',
          requestUrl: req.url,
          timeoutMs: ms,
        });
      }
      return throwError(() => error);
    })
  );
};
